import React from 'react';
import { Helmet } from 'react-helmet';
import { getFullImageUrl } from '../../utils/postMetadata';
import CompanyData from '../../data/CompanyData';

/**
 * SEO Component for managing meta tags
 * Includes Open Graph, Twitter Cards, and JSON-LD structured data
 */
const SEO = ({
    title,
    description,
    image,
    url,
    type = 'article',
    author = 'Dizen',
    keywords,
    datePublished,
    dateModified,
    schema,
    faqSchema,
    lang = 'es',
    noindex = false,
}) => {
    // Default values
    const siteName = CompanyData.companyName;
    const siteTitle = `${CompanyData.companyName} Blog`;
    const twitterHandle = CompanyData.social.tiktok;

    const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
    const fullImageUrl = image
        ? getFullImageUrl(image)
        : `${window.location.origin}/icon.png`;
    const canonicalUrl = url || window.location.href;

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <html lang={lang} />
            <title>{fullTitle}</title>
            {description && <meta name="description" content={description} />}
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={canonicalUrl} />
            {author && <meta name="author" content={author} />}

            {/* Robots */}
            {noindex && <meta name="robots" content="noindex,nofollow" />}

            {/* Open Graph Meta Tags */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={title || siteTitle} />
            {description && (
                <meta property="og:description" content={description} />
            )}
            {image && <meta property="og:image" content={fullImageUrl} />}
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:site_name" content={siteName} />
            {lang && (
                <meta
                    property="og:locale"
                    content={lang === 'es' ? 'es_AR' : lang}
                />
            )}

            {/* Article-specific Open Graph */}
            {type === 'article' && datePublished && (
                <meta
                    property="article:published_time"
                    content={datePublished}
                />
            )}
            {type === 'article' && dateModified && (
                <meta property="article:modified_time" content={dateModified} />
            )}
            {type === 'article' && author && (
                <meta property="article:author" content={author} />
            )}

            {/* Twitter Card Meta Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title || siteTitle} />
            {description && (
                <meta name="twitter:description" content={description} />
            )}
            {image && <meta name="twitter:image" content={fullImageUrl} />}
            {twitterHandle && (
                <meta name="twitter:site" content={twitterHandle} />
            )}
            {twitterHandle && (
                <meta name="twitter:creator" content={twitterHandle} />
            )}

            {/* JSON-LD Structured Data */}
            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            )}
            {faqSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(faqSchema)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
